import { useEffect, useRef, useCallback, useState } from 'react'

const SESSION_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const WARNING_BEFORE_MS = 60 * 1000 // Show warning 1 min before expiry
const ACTIVITY_THROTTLE_MS = 30 * 1000 // Throttle activity detection to every 30s
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const

interface UseSessionTimeoutOptions {
  onTimeout: () => void
  enabled?: boolean
  timeoutMs?: number
  warningBeforeMs?: number
}

interface SessionTimeoutState {
  showWarning: boolean
  secondsRemaining: number
}

export function useSessionTimeout({
  onTimeout,
  enabled = true,
  timeoutMs = SESSION_TIMEOUT_MS,
  warningBeforeMs = WARNING_BEFORE_MS,
}: UseSessionTimeoutOptions): SessionTimeoutState & {
  extendSession: () => void
} {
  const [showWarning, setShowWarning] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(0)

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const onTimeoutRef = useRef(onTimeout)

  // Keep callback ref fresh
  useEffect(() => {
    onTimeoutRef.current = onTimeout
  }, [onTimeout])

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (warningRef.current) clearTimeout(warningRef.current)
    if (countdownRef.current) clearInterval(countdownRef.current)
    timeoutRef.current = null
    warningRef.current = null
    countdownRef.current = null
  }, [])

  const startTimers = useCallback(() => {
    clearAllTimers()
    setShowWarning(false)
    lastActivityRef.current = Date.now()

    // Warning timer: fires (timeout - warningBefore) ms from now
    const warningDelay = timeoutMs - warningBeforeMs
    warningRef.current = setTimeout(() => {
      setShowWarning(true)
      setSecondsRemaining(Math.ceil(warningBeforeMs / 1000))

      // Start countdown
      countdownRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, warningDelay)

    // Actual timeout: fires after full timeout
    timeoutRef.current = setTimeout(() => {
      clearAllTimers()
      setShowWarning(false)
      console.log('[SessionTimeout] Session expired after inactivity')
      onTimeoutRef.current()
    }, timeoutMs)
  }, [timeoutMs, warningBeforeMs, clearAllTimers])

  const extendSession = useCallback(() => {
    startTimers()
  }, [startTimers])

  // Activity listener with throttle
  useEffect(() => {
    if (!enabled) {
      clearAllTimers()
      return
    }

    startTimers()

    const handleActivity = () => {
      const now = Date.now()
      // Throttle: only reset if enough time has passed since last reset
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return
      lastActivityRef.current = now

      // Only reset if warning is NOT showing (once warning shows, user must click "Seguir trabajando")
      if (!warningRef.current) return
      startTimers()
    }

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    // Also handle visibility change — if user comes back to tab, count as activity
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleActivity()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      clearAllTimers()
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [enabled, startTimers, clearAllTimers])

  return { showWarning, secondsRemaining, extendSession }
}
